import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Resizable } from "re-resizable"
import { useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { ButtonTabInlineIcon } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { closeLayer } from "panels/content/side-panel/thunks"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InspectorPanelAccordion } from "features/map/inspector-panel/accordion/InspectorPanelAccordion"
import { config } from "@nova/configuration/browser"
import { DateFormat, formatBytes, TimeFormat } from "@nova/format"
import {
    cyclePreviewIndexLeft,
    cyclePreviewIndexRight,
    selectActivePreviewIndex,
    selectIconImageOrUploadById,
    selectImageAndUploadIdsForIcon,
    selectUploadsForSetSummary,
    setPreviewIndex,
} from "./IconSlice"
import {
    RenderStagedIconImage,
    RenderUploadedIconImage,
} from "features/map/inspector-panel/icons/InspectorPanelIconUpload"
import { getDate } from "features/map/inspector-panel/module"
import { zoomToIcon } from "features/map/inspector-panel/icons/module"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { deleteIconImage } from "features/pubsub/entityActions/images/icon"
import { ImageProcessingState } from "@nova/constants"
import { useIsEditor } from "tools/hooks/useIsEditor"

export type Dimensions = {
    width: number
    height: number
}

const DEFAULT_WIDTH = 475
const DEFAULT_HEIGHT = 580

export function IconImagePreviewContainer({ iconId }: { iconId: string }) {
    const dispatch = useAppDispatch()
    const icon = useAppSelector(selectIconById(iconId))
    const activePreviewIndex = useAppSelector(selectActivePreviewIndex())
    const imageAndUploadIds = useAppSelector(selectImageAndUploadIdsForIcon(iconId))
    const indexIsWithinRange = useMemo(
        () => activePreviewIndex < imageAndUploadIds.length && activePreviewIndex >= 0,
        [activePreviewIndex, imageAndUploadIds.length]
    )
    const activePreviewIconImageOrUpload = useAppSelector(
        indexIsWithinRange
            ? selectIconImageOrUploadById(iconId, imageAndUploadIds[activePreviewIndex].id)
            : () => undefined
    )
    const [dimensions, setDimensions] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
    })
    const [startDimesions, setStartDimensions] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
    })

    useEffect(() => {
        if (typeof window === "undefined") return
        if (imageAndUploadIds.length <= 1) return
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                dispatch(cyclePreviewIndexLeft({ maxIndex: imageAndUploadIds.length - 1 }))
            }
            if (e.key === "ArrowRight") {
                dispatch(cyclePreviewIndexRight({ maxIndex: imageAndUploadIds.length - 1 }))
            }
        }
        window.addEventListener("keydown", handleKeydown)
        return () => {
            window.removeEventListener("keydown", handleKeydown)
        }
    }, [dispatch, imageAndUploadIds.length])

    if (!indexIsWithinRange || !activePreviewIconImageOrUpload) {
        dispatch(closeLayer())
        return null
    }

    return (
        <Resizable
            className="pointer-events-auto m-2"
            minWidth={200}
            minHeight={100}
            maxWidth={"90%"}
            maxHeight={"90%"}
            size={dimensions}
            enable={{ right: true, bottomRight: true, bottom: true }}
            onResizeStop={(...args) => {
                const d = args[3]
                setStartDimensions({
                    width: startDimesions.width + d.width,
                    height: startDimesions.height + d.height,
                })
            }}
            onResize={(...args) => {
                const d = args[3]
                setDimensions({
                    width: startDimesions.width + d.width,
                    height: startDimesions.height + d.height,
                })
            }}
            lockAspectRatio={false}
        >
            <div className="p-2 flex flex-col gap-2 bg-gray-900 w-full rounded-lg">
                <div className="w-full flex items-center justify-between relative">
                    <div className="flex gap-1 overflow-hidden">
                        <small
                            className="text-gray-0 whitespace-nowrap text-ellipsis min-w-0 overflow-hidden"
                            style={{ fontSize: "16px" }}
                        >
                            {activePreviewIconImageOrUpload.fileName || "Image"}
                        </small>
                        <ButtonTabInlineIcon
                            icon={Icons.ZoomTo}
                            onClick={() => {
                                if (!icon) return
                                zoomToIcon(icon)
                            }}
                            className="scale-75 text-gray-100 hover:text-gray-0"
                        />
                    </div>
                    <ButtonTabInlineIcon
                        icon={Icons.Close}
                        onClick={() => {
                            dispatch(closeLayer())
                        }}
                        className="p-0 text-gray-300 hover:text-gray-100"
                        style={{ fontSize: "16px" }}
                    />
                </div>
                <div className="rounded border border-gray-700 overflow-hidden">
                    <div
                        className={classNames("w-full flex justify-center items-center")}
                        style={{ aspectRatio: "5/3" }}
                    >
                        {imageAndUploadIds[activePreviewIndex].type === "Uploaded" ? (
                            <RenderUploadedIconImage
                                entityId={iconId}
                                imageId={activePreviewIconImageOrUpload.id}
                                display="contain"
                                isThumbnail={false}
                            />
                        ) : (
                            <RenderStagedIconImage
                                entityId={iconId}
                                imageId={activePreviewIconImageOrUpload.id}
                                includeOverlay={false}
                                display="contain"
                            />
                        )}
                    </div>
                    {imageAndUploadIds.length > 1 && (
                        <div className="p-2">
                            <IconImageCarousel
                                iconId={iconId}
                                activeIndex={activePreviewIndex}
                                setActiveIndex={(i) => dispatch(setPreviewIndex({ index: i }))}
                            />
                        </div>
                    )}
                </div>
                <ImageInformation iconId={iconId} imageId={activePreviewIconImageOrUpload.id} />
            </div>
        </Resizable>
    )
}

const numThumbailsVisible = 6
const gap = "8px"
// This is really ugly but it works.
// It sets the width of each item such that `numThumbnailsVisible` items are visible no matter the modal width
const flex = `0 0 calc((100% - (${numThumbailsVisible - 1} * ${gap})) / ${numThumbailsVisible})`

function IconImageCarousel({
    iconId,
    activeIndex,
    setActiveIndex,
}: {
    iconId: string
    activeIndex: number
    setActiveIndex: (i: number) => void
}) {
    const iconImagesAndUploads = useAppSelector(selectImageAndUploadIdsForIcon(iconId))
    const imageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
    useEffect(() => {
        if (!imageRefs.current) return
        imageRefs.current.get(activeIndex)?.scrollIntoView({
            inline: "center",
        })
    }, [activeIndex])
    return (
        <div className="w-full flex justify-center">
            <ScrollableArea
                horizontal
                className={classNames("flex nowrap p-1 w-full", {
                    "justify-center": iconImagesAndUploads.length < numThumbailsVisible,
                })}
                style={{ gap }}
            >
                {iconImagesAndUploads.map((image, i) => (
                    <div
                        ref={(el) => {
                            if (el) {
                                imageRefs.current.set(i, el)
                            } else {
                                imageRefs.current.delete(i)
                            }
                        }}
                        key={image.id}
                        className={classNames(
                            "overflow-hidden rounded cursor-pointer flex justify-center items-center border",
                            {
                                "border-blue-500": i === activeIndex,
                                "border-gray-700": i !== activeIndex,
                            }
                        )}
                        style={{
                            aspectRatio: "5/3",
                            flex,
                        }}
                        onClick={() => setActiveIndex(i)}
                    >
                        {image.type === "Uploaded" ? (
                            <RenderUploadedIconImage
                                entityId={iconId}
                                imageId={image.id}
                                isThumbnail
                            />
                        ) : (
                            <RenderStagedIconImage entityId={iconId} imageId={image.id} />
                        )}
                    </div>
                ))}
            </ScrollableArea>
        </div>
    )
}

function ImageInformation({ iconId, imageId }: { iconId: string; imageId: string }) {
    const imageAndUploads = useAppSelector(selectImageAndUploadIdsForIcon(iconId))
    const image = useAppSelector(selectIconImageOrUploadById(iconId, imageId))
    const type = useMemo(
        () => imageAndUploads.find((i) => i.id === imageId)?.type,
        [imageAndUploads, imageId]
    )
    const { organizationSettingDateFormat } = config.use("organizationSettingDateFormat")
    const { organizationSettingTimeFormat } = config.use("organizationSettingTimeFormat")
    const dispatch = useAppDispatch()
    const confirmationModal = useConfirmationModalContext()
    const [isOpen, setIsOpen] = useState(true)
    const date = useMemo(
        () =>
            getDate(
                Number(image?.takenAt),
                organizationSettingDateFormat as DateFormat,
                organizationSettingTimeFormat as TimeFormat
            ),
        [image?.takenAt, organizationSettingDateFormat, organizationSettingTimeFormat]
    )
    const uploadSummary = useAppSelector(selectUploadsForSetSummary(iconId))
    const uploadPercentage = useMemo(() => {
        if (!uploadSummary?.stateCount || !uploadSummary.uploadCount) return 0
        return (
            (uploadSummary.stateCount[ImageProcessingState.UploadDone] || 0) /
            (uploadSummary.uploadCount || 0)
        )
    }, [uploadSummary.stateCount, uploadSummary.uploadCount])
    const isEditor = useIsEditor()
    // const uploadHasFailed = useAppSelector(selectHasUploadFailed(iconId, uploadId || ""))
    const activePreviewIndex = useAppSelector(selectActivePreviewIndex())
    if (!image) return null
    return (
        <InspectorPanelAccordion
            title="Information"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0.5}
            className="!mx-0"
            RightItem={
                type === "Uploaded" && isEditor ? (
                    <Icons.SmallIcon
                        Icon={Icons.Trash}
                        className="text-gray-100 hover:text-red-500 active:text-red-200"
                        onClick={async (e) => {
                            e.stopPropagation()
                            const didConfirm = await confirmationModal.confirm({
                                RenderComponent: getGenericConfirmationModalComponent({
                                    title: "Remove Image?",
                                    message: `This will delete ${image.fileName || "the image"}, do you want to proceed?`,
                                }),
                            })
                            if (didConfirm) {
                                if (
                                    activePreviewIndex === imageAndUploads.length - 1 &&
                                    activePreviewIndex !== 0
                                ) {
                                    dispatch(setPreviewIndex({ index: activePreviewIndex - 1 }))
                                }
                                dispatch(
                                    deleteIconImage({
                                        id: imageId,
                                        context: { flightId: iconId },
                                    })
                                )
                            }
                        }}
                    />
                ) : type === "ToUpload" ? (
                    <h5 className="text-gray-100">Uploading: {uploadPercentage}%</h5>
                ) : undefined
            }
        >
            <div className="flex flex-col gap-1 py-2">
                <div className="flex gap-6 w-full">
                    <ImageInformationStat title="File Name" value={image.fileName || "N/A"} />
                    <ImageInformationStat title="File Size" value={formatBytes(image.size || 0)} />
                </div>
                <div className="flex gap-6 w-full">
                    <ImageInformationStat title="Content Taken" value={date || "N/A"} />
                    <ImageInformationStat
                        title="Dimensions"
                        value={
                            image.imageWidth && image.imageHeight
                                ? `${image.imageWidth} x ${image.imageHeight}`
                                : "N/A"
                        }
                    />
                </div>
                <div className="flex gap-6 w-full">
                    <ImageInformationStat title="Device" value={image.make || "N/A"} />
                    <ImageInformationStat
                        title="Elevation"
                        value={image.altitude?.toString() || "N/A"}
                    />
                </div>
                <div className="flex gap-6 w-full">
                    <ImageInformationStat
                        title="Resolution"
                        value={
                            image.imageWidth && image.imageHeight
                                ? `${image.imageWidth} x ${image.imageHeight}`
                                : "N/A"
                        }
                    />
                    <ImageInformationStat
                        title="Latitude"
                        value={image.latitude ? `${image.latitude}°` : "N/A"}
                    />
                </div>
                <div className="flex gap-6 w-full">
                    <ImageInformationStat
                        title="Focal Length"
                        value={image.focalLength?.toString() || "N/A"}
                    />
                    <ImageInformationStat
                        title="Longitude"
                        value={image.longitude ? `${image.longitude}°` : "N/A"}
                    />
                </div>
            </div>
        </InspectorPanelAccordion>
    )
}

function ImageInformationStat({ title, value }: { title: string; value: string }) {
    return (
        <div className="flex gap-1 justify-between w-full overflow-hidden">
            <small className="text-gray-100" style={{ flexShrink: 0 }}>
                {title}
            </small>
            <small className="text-gray-0 text-right text-wrap break-all">{value}</small>
        </div>
    )
}
